import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { createNoShowAlert } from '../lib/alerts/alertGenerator';

export interface AppointmentItem {
  type: 'producto' | 'servicio' | 'package';
  id: string;
  name: string;
  quantity: number;
}

export interface Appointment {
  id?: string;
  empresa_id: string;
  cliente_id: string;
  webform_submission_id?: string;
  appointment_date: string;
  appointment_time: string;
  duration_minutes: number;
  fulfillment_type: 'pickup' | 'delivery' | 'on-site';
  delivery_address?: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'no-show';
  items: AppointmentItem[];
  notes?: string;
  confirmed_at?: string;
  confirmed_by?: string;
  cancelled_at?: string;
  cancellation_reason?: string;
  created_at?: string;
  updated_at?: string;
}

interface AppointmentsStore {
  appointments: Appointment[];
  isLoading: boolean;
  error: string | null;
  
  fetchAppointments: (empresaId: string, filters?: {
    startDate?: string;
    endDate?: string;
    status?: string;
    clienteId?: string;
  }) => Promise<void>;
  
  createAppointment: (appointment: Omit<Appointment, 'id' | 'created_at' | 'updated_at'>) => Promise<string>;
  updateAppointment: (id: string, updates: Partial<Appointment>) => Promise<void>;
  confirmAppointment: (id: string) => Promise<void>;
  cancelAppointment: (id: string, reason?: string) => Promise<void>;
  rescheduleAppointment: (id: string, newDate: string, newTime: string) => Promise<void>;
  markComplete: (id: string) => Promise<void>;
  markNoShow: (id: string) => Promise<void>;
  clearError: () => void;
}

export const useAppointmentsStore = create<AppointmentsStore>((set, get) => ({
  appointments: [],
  isLoading: false,
  error: null,

  fetchAppointments: async (empresaId: string, filters = {}) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('appointments')
        .select('*')
        .eq('empresa_id', empresaId);

      if (filters.startDate) {
        query = query.gte('appointment_date', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('appointment_date', filters.endDate);
      }
      if (filters.status) {
        query = query.eq('status', filters.status);
      }
      if (filters.clienteId) {
        query = query.eq('cliente_id', filters.clienteId);
      }

      query = query.order('appointment_date', { ascending: true })
        .order('appointment_time', { ascending: true });

      const { data, error } = await query;

      if (error) throw error;
      set({ appointments: (data as Appointment[]) || [], isLoading: false });
    } catch (error: any) {
      console.error('Error fetching appointments:', error);
      set({ error: error.message || 'Error loading appointments', isLoading: false });
    }
  },

  createAppointment: async (appointment: Omit<Appointment, 'id' | 'created_at' | 'updated_at'>) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('appointments')
        .insert({
          ...appointment,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      
      set(state => ({
        appointments: [...state.appointments, data as Appointment],
        isLoading: false,
      }));

      return data.id;
    } catch (error: any) {
      console.error('Error creating appointment:', error);
      set({ error: error.message || 'Error creating appointment', isLoading: false });
      throw error;
    }
  },

  updateAppointment: async (id: string, updates: Partial<Appointment>) => {
    set({ isLoading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('appointments')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      
      set(state => ({
        appointments: state.appointments.map(a => a.id === id ? (data as Appointment) : a),
        isLoading: false,
      }));
    } catch (error: any) {
      console.error('Error updating appointment:', error);
      set({ error: error.message || 'Error updating appointment', isLoading: false });
      throw error;
    }
  },

  confirmAppointment: async (id: string) => {
    const { useAuthStore } = await import('./useAuthStore');
    const authStore = useAuthStore.getState();
    const confirmedBy = authStore.usuario?.id;

    await get().updateAppointment(id, {
      status: 'confirmed',
      confirmed_at: new Date().toISOString(),
      confirmed_by: confirmedBy,
    });
  },

  cancelAppointment: async (id: string, reason?: string) => {
    await get().updateAppointment(id, {
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      cancellation_reason: reason,
    });
  },

  rescheduleAppointment: async (id: string, newDate: string, newTime: string) => {
    await get().updateAppointment(id, {
      appointment_date: newDate,
      appointment_time: newTime,
      status: 'pending', // Reset to pending when rescheduled
      confirmed_at: undefined,
      confirmed_by: undefined,
    });
  },

  markComplete: async (id: string) => {
    await get().updateAppointment(id, {
      status: 'completed',
    });
  },

  markNoShow: async (id: string) => {
    const appt = get().appointments.find(a => a.id === id);
    await get().updateAppointment(id, {
      status: 'no-show',
    });
    if (appt?.empresa_id && appt?.cliente_id) {
      try {
        const { data: cliente } = await supabase
          .from('clientes')
          .select('nombre, apellido')
          .eq('id', appt.cliente_id)
          .maybeSingle();
        const nombre = cliente ? [cliente.nombre, cliente.apellido].filter(Boolean).join(' ').trim() || 'Cliente' : 'Cliente';
        createNoShowAlert(appt.empresa_id, id, nombre, appt.appointment_date, appt.appointment_time).then(() => {});
      } catch {
        // non-blocking
      }
    }
  },

  clearError: () => set({ error: null }),
}));
